"use client";
/**
 * SANATAN DECODE — the CRAFT layer.
 *
 * v18 failed because it had 0.8 lines of authoring per second of video, against v9's 38. The
 * assets were fine; the per-second craft was not. This file carries v9's machinery — word-by-word
 * type, persistent objects that travel and rescale, graphic matches, light sweeps at every cut,
 * print texture — so a beat file can spend all its lines on the beat instead of re-deriving it.
 *
 * ⛔ Everything here is fps-agnostic. The old kit hard-codes 30fps in f() and useStep(); this
 *    episode also renders at 16, where that puts every cue at ~1.9× its real time.
 */
import React from "react";
import {
  AbsoluteFill, Img, staticFile, interpolate, spring, Easing, random,
  useCurrentFrame, useVideoConfig,
} from "remotion";
import { loadFont as loadDeva } from "@remotion/google-fonts/NotoSansDevanagari";
import { WD } from "./gotraWords";
import { wAt } from "./assetDims";

const { fontFamily: devaBase } = loadDeva("normal", {
  weights: ["400", "600", "700", "800", "900"], subsets: ["devanagari", "latin"],
});
export const deva = `${devaBase}, sans-serif`;

export const INK = "#22304F";
export const GOLD = "#BE9433";
export const MARIGOLD = "#D98324";
export const KUMKUM = "#C0392B";       // THE accent — once per beat, never decoration
export const MUTED = "#6A6250";
export const CREAM = "#F7EFDD";

/* ══ TIMING ════════════════════════════════════════════════════
   LAW 1 targets 12-15fps motion, not the literal number 2: step 2 at 30fps, step 1 at 16fps.
   Quantising a 16fps render "on twos" gives 8fps, which is judder rather than Vox. */
export const useStep = () => {
  const { fps } = useVideoConfig();
  const step = Math.max(1, Math.round(fps / 15));
  return Math.floor(useCurrentFrame() / step) * step;
};
export const useF = () => { const { fps } = useVideoConfig(); return (s: number) => s * fps; };

export const useKeys = (keys: [number, number][]) => {
  const frame = useStep(); const F = useF();
  const ts = keys.map((k) => F(k[0]));
  for (let i = 1; i < ts.length; i++) if (ts[i] <= ts[i - 1]) ts[i] = ts[i - 1] + 0.5;
  return interpolate(frame, ts, keys.map((k) => k[1]), {
    extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.inOut(Easing.cubic),
  });
};

export const useSpringAt = (at: number, damping = 9, stiffness = 110, mass = 1.1) => {
  const { fps } = useVideoConfig();
  return spring({ frame: useStep() - at * fps, fps, config: { damping, stiffness, mass } });
};

/* ══ TEXTURE — printed, not rendered ═══════════════════════════ */
export const Halftone: React.FC<{ op?: number }> = ({ op = 0.09 }) => (
  <AbsoluteFill style={{ pointerEvents: "none", opacity: op, mixBlendMode: "multiply", zIndex: 40 }}>
    <svg width="100%" height="100%">
      <defs><pattern id="ht" width="6" height="6" patternUnits="userSpaceOnUse">
        <circle cx="3" cy="3" r="1.15" fill={INK} /></pattern></defs>
      <rect width="100%" height="100%" fill="url(#ht)" />
    </svg>
  </AbsoluteFill>
);

export const Grain: React.FC<{ op?: number }> = ({ op = 0.12 }) => {
  const frame = useStep();
  return (
    <AbsoluteFill style={{ pointerEvents: "none", opacity: op, mixBlendMode: "multiply", zIndex: 41 }}>
      <svg width="100%" height="100%">
        <filter id="gr"><feTurbulence type="fractalNoise" baseFrequency="0.92" numOctaves="3" seed={frame % 5} /></filter>
        <rect width="100%" height="100%" filter="url(#gr)" />
      </svg>
    </AbsoluteFill>
  );
};

/** Warm sweep across the frame at every cut, so a transition reads as light moving, not a jump. */
export const LightSweep: React.FC<{ at: number }> = ({ at }) => {
  const frame = useStep(); const F = useF();
  const p = interpolate(frame, [F(at) - 0.15 * 30, F(at) + 0.7 * 30], [0, 1], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.inOut(Easing.quad),
  });
  if (p <= 0 || p >= 1) return null;
  return (
    <AbsoluteFill style={{ pointerEvents: "none", overflow: "hidden", zIndex: 30 }}>
      <div style={{
        position: "absolute", top: -200, bottom: -200, width: 420, left: -500 + p * 2000,
        transform: "rotate(12deg)", filter: "blur(22px)", opacity: Math.sin(Math.PI * p) * 0.9,
        background: "linear-gradient(90deg, rgba(255,244,214,0) 0%, rgba(255,244,214,0.85) 50%, rgba(255,244,214,0) 100%)",
      }} />
    </AbsoluteFill>
  );
};

/* ══ WORD — lands on the exact frame it is spoken ══════════════ */
export const W: React.FC<{
  i: number; size?: number; color?: string; weight?: number;
  strike?: boolean; under?: boolean; out?: number; box?: boolean;
}> = ({ i, size = 54, color = INK, weight = 800, strike, under, out, box }) => {
  const w = WD[i];
  const frame = useStep(); const F = useF();
  const sIn = useSpringAt(w ? w[0] : 1e6, 9, 150, 0.8);
  const sOut = useSpringAt(out ?? 1e6, 200, 80, 0.5);
  const grow = interpolate(frame, [F(w ? w[0] : 0) + 2, F(w ? w[0] : 0) + 12], [0, 1],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.out(Easing.cubic) });
  if (!w) return null;
  return (
    <span style={{
      position: "relative", display: "inline-block",
      fontFamily: deva, fontWeight: weight, fontSize: size, lineHeight: 1.36, color,
      padding: box ? "2px 12px" : undefined,
      background: box ? "rgba(255,251,240,0.82)" : undefined,
      borderRadius: box ? 6 : undefined,
      textShadow: "0 0 26px rgba(255,251,240,0.98), 0 0 10px rgba(255,251,240,0.95), 0 2px 2px rgba(255,251,240,0.8)",
      opacity: Math.min(1, sIn * 1.5) * (1 - Math.min(1, sOut * 1.4)),
      transform: `translateY(${(1 - sIn) * 32 + sOut * 84}px) rotate(${8 * (1 - sIn) - 13 * sOut}deg) scale(${0.87 + sIn * 0.13})`,
      transformOrigin: "50% 140%",
    }}>
      {w[2]}
      {strike && <span style={{ position: "absolute", left: -4, top: "53%", height: Math.max(3, size * 0.075), width: `calc((100% + 8px) * ${grow})`, background: KUMKUM, borderRadius: 4 }} />}
      {under && <span style={{ position: "absolute", left: 0, bottom: -5, height: Math.max(3, size * 0.06), width: `calc(100% * ${grow})`, background: GOLD, borderRadius: 3, opacity: 0.85 }} />}
    </span>
  );
};

/** A run of consecutive words, each landing on its own timestamp.
    ⛔ IN FLOW — no left/top. Put it inside a <Panel>. Absolute-positioning text is what made
    v19-act1 overlap: Devanagari width is unknowable at author time, so a long run wrapped past
    its allotted box and collided with the block below. Flow layout cannot do that. */
export const Run: React.FC<{
  from: number; to: number; size?: number; color?: string; weight?: number;
  out?: number; gap?: number;
  accentAt?: number; accent?: string; strikeAt?: number; underAt?: number; center?: boolean;
}> = ({ from, to, size = 50, color = INK, weight = 800, out, gap = 13,
       accentAt, accent = KUMKUM, strikeAt, underAt, center }) => (
  <div style={{
    display: "flex", flexWrap: "wrap",
    gap: `2px ${gap}px`, justifyContent: center ? "center" : "flex-start",
  }}>
    {Array.from({ length: to - from + 1 }, (_, k) => (
      <W key={from + k} i={from + k} size={size} weight={weight} out={out}
        color={accentAt === from + k ? accent : color}
        strike={strikeAt === from + k} under={underAt === from + k} />
    ))}
  </div>
);

/* ══ PROP — swings in, idles on a pendulum, swings out ═════════ */
export const SHADOW = "drop-shadow(0 24px 26px rgba(72,46,16,0.40)) drop-shadow(0 2px 3px rgba(72,46,16,0.30))";

export const Prop: React.FC<{
  src: string; h: number; left: number; top: number; at: number; out: number;
  from?: 1 | -1; z?: number; flip?: boolean; rot?: number;
}> = ({ src, h, left, top, at, out, from = 1, z = 12, flip, rot = 0 }) => {
  const frame = useStep(); const F = useF();
  const sIn = useSpringAt(at, 8, 95, 1.15);
  const sOut = useSpringAt(out, 200, 75, 0.5);
  const pend = Math.sin((frame - F(at)) / 34) * 1.3 * sIn * (1 - sOut);
  if (frame < F(at) - 34 || frame > F(out) + 42) return null;
  return (
    <Img data-audit="prop" src={src} style={{
      position: "absolute", left, top, height: h, zIndex: z, filter: SHADOW,
      transform: `translate(${230 * from * (1 - sIn) - 330 * from * sOut}px, ${64 * (1 - sIn) + 180 * sOut}px) `
        + `rotate(${rot + 13 * from * (1 - sIn) + pend - 26 * from * sOut}deg) `
        + `scale(${(0.9 + 0.1 * sIn - 0.12 * sOut)}) ${flip ? "scaleX(-1)" : ""}`,
      transformOrigin: "50% -26%",
      opacity: Math.min(1, sIn * 1.6) * (1 - Math.min(1, sOut * 1.4)),
    }} />
  );
};

/** A PERSISTENT prop — one instance that travels and rescales between poses (LAW 3).
    poses: [second, left, top, height][] — it is never removed and re-created. */
export const Persist: React.FC<{
  src: string; poses: [number, number, number, number][]; at: number; out: number; z?: number; flip?: boolean;
}> = ({ src, poses, at, out, z = 14, flip }) => {
  const frame = useStep(); const F = useF();
  const sIn = useSpringAt(at, 8, 90, 1.1);
  const sOut = useSpringAt(out, 200, 70, 0.5);
  const left = useKeys(poses.map((p) => [p[0], p[1]] as [number, number]));
  const top = useKeys(poses.map((p) => [p[0], p[2]] as [number, number]));
  const h = useKeys(poses.map((p) => [p[0], p[3]] as [number, number]));
  if (frame < F(at) - 30 || frame > F(out) + 42) return null;
  const pend = Math.sin((frame - F(at)) / 36) * 1.15 * sIn * (1 - sOut);
  return (
    <div style={{
      position: "absolute", left, top, zIndex: z,
      transform: `translate(${-250 * (1 - sIn)}px, ${-160 * (1 - sIn) + 200 * sOut}px) rotate(${13 * (1 - sIn) + pend - 26 * sOut}deg)`,
      transformOrigin: "50% -28%",
      opacity: Math.min(1, sIn * 1.6) * (1 - Math.min(1, sOut * 1.4)),
    }}>
      <Img src={src} style={{ height: h, filter: SHADOW, transform: flip ? "scaleX(-1)" : undefined }} />
    </div>
  );
};

/** Pack props by their REAL pixel widths — overlap becomes arithmetically impossible. */
/** h is required by PackRow (it packs at a given height) and ignored by GridToStrip
    (which derives heights from its own grid/strip geometry). */
export type Item = { k: string; h?: number; at?: number; photo?: boolean; flip?: boolean; label?: string };
export const PackRow: React.FC<{
  items: Item[]; out: number; baseline: number; x: number; w: number; gap?: number; at?: number; z?: number;
}> = ({ items, out, baseline, x, w, gap = 20, at = 0, z = 12 }) => {
  const ws = items.map((it) => wAt((it.photo ? "photo/" : "cut/") + it.k, it.h ?? 200));
  const total = ws.reduce((a, b) => a + b, 0) + gap * (items.length - 1);
  const s = total > w ? w / total : 1;
  let cx = x + (w - total * s) / 2;
  return (
    <>
      {items.map((it, i) => {
        const h = (it.h ?? 200) * s, iw = ws[i] * s, left = cx;
        cx += iw + gap * s;
        return (
          <React.Fragment key={it.k + i}>
            <Prop src={staticFile(`sanatan/${it.photo ? "photo" : "cut"}/${it.k}.png`)}
              h={h} left={left} top={baseline - h} at={it.at ?? at} out={out}
              from={i % 2 === 0 ? 1 : -1} z={z + i} flip={it.flip} />
            {/* ⚡ the label is placed by the PACKER, centred on the prop's real computed x —
                hand-typed label positions are what made them collide with each other. */}
            {it.label && <CenterChip text={it.label} cx={left + iw / 2} y={baseline + 10}
              at={(it.at ?? at) + 0.08} out={out} />}
          </React.Fragment>
        );
      })}
    </>
  );
};

/** ⭐ GRID → STRIP.  Yash, 2026-08-11: "you can show things in big when you are making them
    appear ... we can [use the] entire canvas, 3 each row, and then shift all those to the bottom,
    to make the canvas full great and brilliant."

    Each item arrives LARGE in a 3-across grid cell on its own spoken word, then at `shiftAt` all
    of them travel and shrink into the bottom strip together. One object per item throughout —
    it is the same node moving, never a big copy swapped for a small one (LAW 3). */
export const GridToStrip: React.FC<{
  items: Item[]; shiftAt: number; out: number;
  cols?: number; gridTop?: number; gridH?: number; bigH?: number;
  stripBaseline?: number; stripH?: number; stripX?: number; stripW?: number; gap?: number; z?: number;
}> = ({ items, shiftAt, out, cols = 3, gridTop = 96, gridH = 250, bigH = 205,
        stripBaseline = 612, stripH = 112, stripX = 26, stripW = 1228, gap = 14, z = 12 }) => {
  const n = items.length;
  // strip layout, packed by real widths
  const sw = items.map((it) => wAt((it.photo ? "photo/" : "cut/") + it.k, stripH));
  const tot = sw.reduce((a, b) => a + b, 0) + gap * (n - 1);
  const sc = tot > stripW ? stripW / tot : 1;
  let cx = stripX + (stripW - tot * sc) / 2;
  const strip = sw.map((w) => { const L = cx; cx += w * sc + gap * sc; return L; });

  return (
    <>
      {items.map((it, i) => {
        const col = i % cols, row = Math.floor(i / cols);
        const cellW = 1280 / cols;
        const gw = wAt((it.photo ? "photo/" : "cut/") + it.k, bigH);
        const gx = col * cellW + (cellW - gw) / 2;
        const gy = gridTop + row * gridH + (gridH - bigH) / 2;
        const asp = gw / bigH;                       // width per unit height — for label centring
        return <MoveCell key={it.k + i} src={staticFile(`sanatan/${it.photo ? "photo" : "cut"}/${it.k}.png`)}
          label={it.label} at={it.at ?? 0} shiftAt={shiftAt} out={out} z={z + i}
          gx={gx} gy={gy} gh={bigH} sx={strip[i]} sy={stripBaseline - stripH * sc} sh={stripH * sc}
          asp={asp} />;
      })}
    </>
  );
};

const MoveCell: React.FC<{
  src: string; label?: string; at: number; shiftAt: number; out: number; z: number;
  gx: number; gy: number; gh: number; sx: number; sy: number; sh: number; asp: number;
}> = ({ src, label, at, shiftAt, out, z, gx, gy, gh, sx, sy, sh, asp }) => {
  const frame = useStep(); const F = useF();
  const sIn = useSpringAt(at, 8, 96, 1.12);
  const sOut = useSpringAt(out, 200, 75, 0.5);
  const x = useKeys([[at, gx], [shiftAt, gx], [shiftAt + 0.85, sx]]);
  const y = useKeys([[at, gy], [shiftAt, gy], [shiftAt + 0.85, sy]]);
  const h = useKeys([[at, gh], [shiftAt, gh], [shiftAt + 0.85, sh]]);
  if (frame < F(at) - 32 || frame > F(out) + 42) return null;
  const o = Math.min(1, sIn * 1.6) * (1 - Math.min(1, sOut * 1.4));
  const pend = Math.sin((frame - F(at)) / 36) * 1.15 * sIn * (1 - sOut);
  return (
    <>
      <img data-audit="prop" src={src} style={{
        position: "absolute", left: x, top: y, height: h, zIndex: z, filter: SHADOW, opacity: o,
        transform: `translateY(${70 * (1 - sIn) + 170 * sOut}px) rotate(${11 * (1 - sIn) + pend - 24 * sOut}deg) scale(${0.88 + 0.12 * sIn})`,
        transformOrigin: "50% -26%",
      }} />
      {/* centre on the item's MIDDLE — h * aspect is its real current width */}
      {label && <CenterChip text={label} cx={x + (h * asp) / 2} y={y + h + 8} at={at + 0.1} out={out}
        size={h > 150 ? 23 : 17} />}
    </>
  );
};

/** A chip that centres itself on a given x — used for prop labels, where the x is computed. */
export const CenterChip: React.FC<{ text: string; cx: number; y: number; at: number; out?: number; size?: number; color?: string; z?: number }> =
  ({ text, cx, y, at, out, size = 19, color = MUTED, z = 19 }) => {
    const sIn = useSpringAt(at, 9, 165, 0.8);
    const sOut = useSpringAt(out ?? 1e6, 200, 80, 0.5);
    const o = Math.min(1, sIn * 1.5) * (1 - Math.min(1, sOut * 1.4));
    if (o <= 0.01) return null;
    return (
      <div data-audit="chip" style={{
        position: "absolute", left: cx, top: y, zIndex: z, opacity: o,
        transform: `translateX(-50%) translateY(${(1 - sIn) * 12}px)`,
        fontFamily: deva, fontSize: size, fontWeight: 700, color, whiteSpace: "nowrap",
        background: "rgba(255,251,240,0.88)", padding: "2px 9px", borderRadius: 5,
      }}>{text}</div>
    );
  };

/* ══ DRAWN MARKS — the diagram vocabulary ══════════════════════ */
/** A line that draws itself on, then can retract. */
export const Stroke: React.FC<{
  d: string; at: number; out?: number; color?: string; w?: number; dur?: number; dash?: boolean; z?: number;
}> = ({ d, at, out, color = INK, w = 3, dur = 0.55, dash, z = 15 }) => {
  const frame = useStep(); const F = useF();
  const p = interpolate(frame, [F(at), F(at + dur)], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.inOut(Easing.cubic) });
  const o = out ? interpolate(frame, [F(out), F(out + 0.4)], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }) : 1;
  if (p <= 0 || o <= 0) return null;
  return (
    <svg width={1280} height={720} style={{ position: "absolute", left: 0, top: 0, zIndex: z, pointerEvents: "none", overflow: "visible" }}>
      <path d={d} fill="none" stroke={color} strokeWidth={w} strokeLinecap="round"
        strokeDasharray={dash ? "9 10" : "2200"} strokeDashoffset={dash ? 0 : 2200 * (1 - p)}
        opacity={0.9 * o} pathLength={dash ? undefined : 2200} />
    </svg>
  );
};

/** A dot that pops on its word and can pulse once. */
export const Dot: React.FC<{ x: number; y: number; r?: number; at: number; out?: number; color?: string; z?: number; ring?: boolean }> =
  ({ x, y, r = 8, at, out, color = KUMKUM, z = 16, ring }) => {
    const sIn = useSpringAt(at, 7, 190, 0.7);
    const sOut = useSpringAt(out ?? 1e6, 200, 80, 0.5);
    const o = Math.min(1, sIn * 1.6) * (1 - Math.min(1, sOut * 1.4));
    if (o <= 0.01) return null;
    return (
      <div style={{
        position: "absolute", left: x - r, top: y - r, width: r * 2, height: r * 2, zIndex: z,
        borderRadius: "50%", background: color, opacity: o,
        transform: `scale(${0.4 + sIn * 0.6})`,
        boxShadow: ring ? `0 0 0 ${r * 0.55}px rgba(192,57,43,0.18)` : "0 2px 6px rgba(72,46,16,0.35)",
      }} />
    );
  };

/** A label chip pinned to a point — the map's naming device. */
export const Chip: React.FC<{ text: string; x: number; y: number; at: number; out?: number; color?: string; size?: number; z?: number }> =
  ({ text, x, y, at, out, color = INK, size = 22, z = 17 }) => {
    const sIn = useSpringAt(at, 9, 165, 0.8);
    const sOut = useSpringAt(out ?? 1e6, 200, 80, 0.5);
    const o = Math.min(1, sIn * 1.5) * (1 - Math.min(1, sOut * 1.4));
    if (o <= 0.01) return null;
    return (
      <div data-audit="chip" style={{
        position: "absolute", left: x, top: y, zIndex: z, opacity: o,
        transform: `translateY(${(1 - sIn) * 14}px) scale(${0.9 + sIn * 0.1})`,
        fontFamily: deva, fontSize: size, fontWeight: 700, color,
        background: "rgba(255,251,240,0.9)", padding: "3px 11px", borderRadius: 5,
        border: `1.5px solid ${color}33`, whiteSpace: "nowrap",
        boxShadow: "0 3px 9px rgba(72,46,16,0.18)",
      }}>{text}</div>
    );
  };

/** Free line of type (a gloss, a caption) that still obeys the swing law. */
export const Cap: React.FC<{ text: string; at: number; out: number; size?: number; color?: string; weight?: number }> =
  ({ text, at, out, size = 28, color = MUTED, weight = 700 }) => {
    const sIn = useSpringAt(at, 9, 160, 0.8);
    const sOut = useSpringAt(out, 200, 80, 0.5);
    const o = Math.min(1, sIn * 1.5) * (1 - Math.min(1, sOut * 1.4));
    return (
      <div style={{
        width: "100%",
        fontFamily: deva, fontSize: size, fontWeight: weight, lineHeight: 1.34, color, opacity: o,
        textShadow: "0 0 24px rgba(255,251,240,0.99), 0 0 10px rgba(255,251,240,0.96)",
        transform: `translateY(${(1 - sIn) * 18 + sOut * 34}px)`,
      }}>{text}</div>
    );
  };

/** THE LAYOUT BOX. Everything textual goes in one of these. Children stack in normal flow with a
    guaranteed gap, so two blocks can never occupy the same pixels no matter how the text wraps.
    Blocks keep their space when they animate out — that is deliberate; reflowing mid-beat is
    what makes type jump around. */
export const Panel: React.FC<{
  x: number; y: number; w: number; gap?: number; z?: number; align?: "left" | "center";
  at?: number; out?: number; children: React.ReactNode;
}> = ({ x, y, w, gap = 16, z = 18, align = "left", at, out, children }) => {
  const frame = useStep(); const F = useF();
  // ⛔ A Panel keeps its full measured box even when its children have faded to zero, and <Beat>
  //    holds adjacent beats on screen together for ~1.6s of lead-in/out. That combination put two
  //    beats' panels in the same slot — the 14,802px² collision the auditor caught at 28.5-30.0s.
  //    A Panel must therefore unmount on its own schedule, not the beat's.
  if (at !== undefined && frame < F(at) - 6) return null;
  if (out !== undefined && frame > F(out) + 6) return null;
  return (
  <div data-audit="panel" style={{
    position: "absolute", left: x, top: y, width: w, zIndex: z,
    display: "flex", flexDirection: "column", gap, alignItems: align === "center" ? "center" : "flex-start",
  }}>{children}</div>
  );
};

/** Falling marigold petals — atmosphere, used sparingly. */
export const Petals: React.FC<{ n?: number; op?: number }> = ({ n = 14, op = 0.22 }) => {
  const frame = useStep();
  return (
    <AbsoluteFill style={{ pointerEvents: "none", zIndex: 20 }}>
      {Array.from({ length: n }, (_, i) => {
        const s = `p${i}`, sp = 0.4 + random(s + "s") * 0.7;
        const y = ((random(s + "y") * 800 + frame * sp) % 830) - 60;
        const r = 3 + random(s + "r") * 4.5;
        return <div key={i} style={{
          position: "absolute", left: random(s) * 1280 + Math.sin((frame + i * 40) / 34) * 30, top: y,
          width: r * 2, height: r * 1.25, borderRadius: "50%", background: MARIGOLD, opacity: op,
          transform: `rotate(${frame * 1.4 + i * 30}deg)`,
        }} />;
      })}
    </AbsoluteFill>
  );
};

/** Unmount gate with lead-in/out so beats overlap instead of cutting dead. */
export const Beat: React.FC<{ a: number; b: number; children: React.ReactNode }> = ({ a, b, children }) => {
  const frame = useStep(); const F = useF();
  if (frame < F(a - 0.3) || frame >= F(b + 0.4)) return null;
  return <AbsoluteFill style={{ zIndex: 10 }}>{children}</AbsoluteFill>;
};


/* ══ LAYOUT AUDITOR ════════════════════════════════════════════
   Mount this and every element carrying data-audit reports its real rendered box to the browser
   console. audit_overlap.mjs samples frames, reads those logs and fails on any collision.
   ⛔ Eyeballing frames does not catch overlap — this does. */
export const AuditProbe: React.FC = () => {
  const frame = useCurrentFrame();
  React.useEffect(() => {
    const boxes = Array.from(document.querySelectorAll("[data-audit]")).map((e) => {
      const r = (e as HTMLElement).getBoundingClientRect();
      const st = getComputedStyle(e as HTMLElement);
      return {
        n: e.getAttribute("data-audit"), x: +r.x.toFixed(1), y: +r.y.toFixed(1),
        w: +r.width.toFixed(1), h: +r.height.toFixed(1), o: +(+st.opacity).toFixed(2),
        t: ((e as HTMLElement).innerText || "").slice(0, 22).replace(/\s+/g, " "),
      };
    });
    // eslint-disable-next-line no-console
    console.log("AUDIT::" + JSON.stringify({ frame, boxes }));
  });
  return null;
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
